import * as React from 'react';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { DASHBOARD_LOCALE, dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { createChannelGroupSeeds, type ChannelGroup } from '../../model';
import { ChannelGroupDialog } from '../ChannelGroupDialog/ChannelGroupDialog';

const channelGroupDateFormatter = new Intl.DateTimeFormat(DASHBOARD_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function ChannelGroupsPage() {
  const { can } = useShigoPermissions();
  const canCreate = can(ShigoPermission.MadBuyCampaignWrite);
  const [channelGroups, setChannelGroups] = React.useState(createChannelGroupSeeds);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const columns = buildDashboardDataTableColumns<ChannelGroup>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('channelGroups.name'),
      value: (channelGroup) => channelGroup.name,
      headerClassName: 'w-[60%]',
      className: 'w-[60%] font-medium',
    },
    {
      id: 'channels',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('channelGroups.channels'),
      value: (channelGroup) => channelGroup.channelsCount,
      headerClassName: 'w-32',
      className: 'w-32 tabular-nums',
    },
    {
      id: 'updated',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('channelGroups.updated'),
      value: (channelGroup) => channelGroupDateFormatter.format(new Date(channelGroup.updatedAt)),
      headerClassName: 'w-36',
      className: 'w-36 whitespace-nowrap',
    },
  ]);

  function addChannelGroup(channelGroup: ChannelGroup) {
    setChannelGroups((current) => {
      const duplicate = current.some(
        (existing) => existing.name.toLowerCase() === channelGroup.name.toLowerCase(),
      );
      return duplicate ? current : [channelGroup, ...current];
    });
    setDialogOpen(false);
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT('channelGroups.title')}
        tableLabel={dashboardT('channelGroups.title')}
        columns={columns}
        items={channelGroups}
        getRowKey={(channelGroup) => channelGroup.name}
        emptyMessage={dashboardT('channelGroups.empty')}
        minWidthClassName="min-w-[560px]"
        toolbar={
          canCreate ? (
            <Button
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {dashboardT('channelGroups.create')}
            </Button>
          ) : null
        }
      />
      {canCreate && dialogOpen ? (
        <ChannelGroupDialog
          onClose={() => {
            setDialogOpen(false);
          }}
          onCreate={addChannelGroup}
        />
      ) : null}
    </main>
  );
}
